import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { authApiSlice } from './authApiSlice';
import { useNavigate } from 'react-router-dom';
import { CUSTOMERS, JWT_TOKEN, JWT_TOKEN_EXPIRES_AT, REFRESH_TOKEN } from '../../../storagekeys';
import { REHYDRATE } from 'redux-persist';
import { PURGE } from "redux-persist";
import { ENV_TYPES } from '../../../components/global/Sidebar';

export const UserState = {
  UNSET: 'unset',
  LOGGED_IN: 'loggedIn',
  LOGGED_OUT: 'loggedOut',
  LOGGING_IN: 'loggingIn',
  LOGGING_OUT: 'loggingOut'
};

const initialState = {
  status: UserState.LOGGED_OUT,
  token: null,
  refreshToken: null,
  relations: null,
  customers: null,
  expiresAt: null,
  permissions: null,
  user: null,
}

// Return copies of the lists with the user's chosen main environment hoisted to
// the front of its own group. Non-mutating on purpose: the login payload arrays
// are frozen, so sorting them in place throws — we sort a copy instead.
// Relations match on internalId (the stable root id); login rewrites relation.id
// to the per-customer externalId, so id is not a safe key. Customers match on id.
const sortByDefaultEnvironment = (customers, relations, defaultEnvironment) => {
  let sortedCustomers = customers;
  let sortedRelations = relations;
  if (defaultEnvironment) {
    if (defaultEnvironment.type === ENV_TYPES[100] && Array.isArray(customers)) {
      sortedCustomers = [...customers].sort((a, b) => Number(b.id === defaultEnvironment.id) - Number(a.id === defaultEnvironment.id));
    }
    if (defaultEnvironment.type === ENV_TYPES[200] && Array.isArray(relations)) {
      sortedRelations = [...relations].sort((a, b) => Number(b.internalId === defaultEnvironment.id) - Number(a.internalId === defaultEnvironment.id));
    }
  }
  return { customers: sortedCustomers, relations: sortedRelations };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    updateToken: (state, action) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.expiresAt = action.payload.expires_at;
    },
    logout: (state, action) => {
      state.status = UserState.LOGGED_OUT;
        state.token = null;
        state.refreshToken = null;
        state.customers = null;
        state.relations = null;
        state.expiresAt = null;
        state.user = null;
    },

    updateAuthData: (state, action) => {
      const payload = action.payload;

      if (payload.token) state.token = payload.token;
      if (payload.refreshToken) state.refreshToken = payload.refreshToken;
      if (payload.expires_at) state.expiresAt = payload.expires_at;

      // Update or add the new relation
      if (payload.addedRelation) {
        if (!state.relations) state.relations = [];

        const existingIndex = state.relations.findIndex(
          (rel) => rel.id === payload.addedRelation.id
        );

        if (existingIndex !== -1) {
          // Replace existing relation
          state.relations[existingIndex] = payload.addedRelation;
        } else {
          // Add new relation
          state.relations.push(payload.addedRelation);
        }
      }

      if(payload.addedCustomer) { 
        if(!state.customers) state.customers = [];

        const existingIndex = state.customers.findIndex(
          (rel) => rel.id === payload.addedCustomer.id
        );

        if (existingIndex !== -1) {
          // Replace existing relation
          state.customers[existingIndex] = payload.addedCustomer;
        } else {
          // Add new relation
          state.customers.push(payload.addedCustomer);
        }
      }

      // Update other fields if present
      if (payload.user) state.user = payload.user;
      if (payload.permissions) state.permissions = payload.permissions;
    },
    removeRelationById: (state, action) => {
      const idToRemove = action.payload;
  
      if (state.relations) {
        state.relations = state.relations.filter(rel => rel.id !== idToRemove);
      }
  
  
      if (state.permissions) {
        state.permissions = state.permissions.filter(p => p.relationId !== idToRemove);
      }
  
    },
    removeCustomerById: (state, action) => {
      const idToRemove = action.payload;

      if (state.customers) {
        state.customers = state.customers.filter(rel => rel.id !== idToRemove);
      }


      if (state.permissions) {
        state.permissions = state.permissions.filter(p => p.relationId !== idToRemove);
      }

    },
    // Update the stored main environment after the user saves it, and re-sort the
    // rail so the change shows immediately without needing to log in again.
    // Payload is { type, id } or null to clear the preference.
    setDefaultEnvironment: (state, action) => {
      if (state.user) {
        state.user.defaultEnvironment = action.payload;
      }
      const sorted = sortByDefaultEnvironment(state.customers, state.relations, action.payload);
      state.customers = sorted.customers;
      state.relations = sorted.relations;
    }
  },
  extraReducers: (builder) => {
    builder
      // ... your other matchers
      .addCase(REHYDRATE, (state, action) => {
        // Or if you want to update the state with the rehydrated state:
        if (action.payload) {
          return { ...state, ...action.payload.auth };
        }
        return state;
      })
      .addCase(PURGE, () => initialState) // THIS LINE
      .addCase(checkAndRedirect.fulfilled, (state) => {
        // You can keep this empty or do additional state modifications if required.
      })
      .addMatcher(authApiSlice.endpoints.login.matchPending, (state) => {
        state.status = UserState.LOGGING_IN;
      })
      // .addMatcher(authApiSlice.endpoints.refreshToken.matchPending, (state) => {
      //   console.log("kwakwastate",state);
      //   // state.status = UserState.LOGGING_IN;
      // })
      .addMatcher(authApiSlice.endpoints.login.matchFulfilled, (state, action) => {
        // Skip if MFA is required — tokens aren't issued yet
        if (action.payload.data.mfaRequired) {
          return;
        }
        state.status = UserState.LOGGED_IN;
        state.token = action.payload.data.token;
        state.customers = action.payload.data.customers;
        state.relations = action.payload.data.relations;
        state.expiresAt = action.payload.data.expires_at;
        state.refreshToken = action.payload.data.refresh_token;
        state.permissions = action.payload.data.permissions;
        state.user = action.payload.data.user;

        // Float the user's chosen main environment to the front of its group.
        const sortedEnvironments = sortByDefaultEnvironment(action.payload.data.customers, action.payload.data.relations, action.payload.data.user?.defaultEnvironment);
        state.customers = sortedEnvironments.customers;
        state.relations = sortedEnvironments.relations;

        const customerPermissions = action.payload.data.permissions.filter(x => !x.relationId);
        const groupedByCustomer = customerPermissions.reduce((acc, wp) => {
          if (!acc[wp.customerId]) {
            acc[wp.customerId] = [];
          }
          acc[wp.customerId].push(wp.warehouseId);
          return acc;
        }, {});

        state.customerWarehouses = Object.entries(groupedByCustomer)
          .filter(([_, warehouses]) => warehouses.length === 1 && warehouses[0] > 0)
          .map(([customerId, warehouses]) => ({
            customerId: parseInt(customerId),
            warehouseId: warehouses[0]
        }));
      })
      .addMatcher((action) => action.type === 'rootApi/executeMutation/fulfilled' && action.meta?.arg?.endpointName === 'verifyMfa', (state, action) => {
        state.status = UserState.LOGGED_IN;
        state.token = action.payload.data.token;
        state.customers = action.payload.data.customers;
        state.relations = action.payload.data.relations;
        state.expiresAt = action.payload.data.expires_at;
        state.refreshToken = action.payload.data.refresh_token;
        state.permissions = action.payload.data.permissions;
        state.user = action.payload.data.user;

        // Float the user's chosen main environment to the front of its group.
        const sortedEnvironments = sortByDefaultEnvironment(action.payload.data.customers, action.payload.data.relations, action.payload.data.user?.defaultEnvironment);
        state.customers = sortedEnvironments.customers;
        state.relations = sortedEnvironments.relations;

        const customerPermissions = action.payload.data.permissions.filter(x => !x.relationId);
        const groupedByCustomer = customerPermissions.reduce((acc, wp) => {
          if (!acc[wp.customerId]) {
            acc[wp.customerId] = [];
          }
          acc[wp.customerId].push(wp.warehouseId);
          return acc;
        }, {});

        state.customerWarehouses = Object.entries(groupedByCustomer)
          .filter(([_, warehouses]) => warehouses.length === 1 && warehouses[0] > 0)
          .map(([customerId, warehouses]) => ({
            customerId: parseInt(customerId),
            warehouseId: warehouses[0]
        }));
      })
      .addMatcher((action) => action.type === 'rootApi/executeMutation/fulfilled' && action.meta?.arg?.endpointName === 'useRecoveryCode', (state, action) => {
        state.status = UserState.LOGGED_IN;
        state.token = action.payload.data.token;
        state.customers = action.payload.data.customers;
        state.relations = action.payload.data.relations;
        state.expiresAt = action.payload.data.expires_at;
        state.refreshToken = action.payload.data.refresh_token;
        state.permissions = action.payload.data.permissions;
        state.user = action.payload.data.user;

        // Float the user's chosen main environment to the front of its group.
        const sortedEnvironments = sortByDefaultEnvironment(action.payload.data.customers, action.payload.data.relations, action.payload.data.user?.defaultEnvironment);
        state.customers = sortedEnvironments.customers;
        state.relations = sortedEnvironments.relations;

        const customerPermissions = action.payload.data.permissions.filter(x => !x.relationId);
        const groupedByCustomer = customerPermissions.reduce((acc, wp) => {
          if (!acc[wp.customerId]) {
            acc[wp.customerId] = [];
          }
          acc[wp.customerId].push(wp.warehouseId);
          return acc;
        }, {});

        state.customerWarehouses = Object.entries(groupedByCustomer)
          .filter(([_, warehouses]) => warehouses.length === 1 && warehouses[0] > 0)
          .map(([customerId, warehouses]) => ({
            customerId: parseInt(customerId),
            warehouseId: warehouses[0]
        }));
      })
      .addMatcher(authApiSlice.endpoints.logout.matchPending, (state) => {
        state.status = UserState.LOGGING_OUT;
      })
      .addMatcher(authApiSlice.endpoints.logout.matchFulfilled, (state) => {
        state.status = UserState.LOGGED_OUT;
        state.token = null;
        state.refreshToken = null;
        state.customers = null;
        state.relations = null;
        state.expiresAt = null;
        state.user = null;
      })
      .addMatcher(authApiSlice.endpoints.logout.matchRejected, (state) => {
        state.status = UserState.LOGGED_OUT;
        state.token = null;
        state.refreshToken = null;
        state.customers = null;
        state.relations = null;
        state.expiresAt = null;
        state.user = null;
      })
      // Store the whole rotated pair. The reauth flow dispatches updateToken with
      // the same payload, but a reply that lands on its own — after that flow
      // abandoned and aborted it — must not advance the access token while
      // leaving the rotated refresh token behind.
      .addMatcher(authApiSlice.endpoints.refreshToken.matchFulfilled, (state, action) => {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.expiresAt = action.payload.expires_at;
      });
  },
});

// Async Thunk for checking auth status and redirecting
export const checkAndRedirect = createAsyncThunk(
  'auth/checkAndRedirect',
  async (permissions, { dispatch, getState }) => {
    const state = getState();
    const authStatus = state.auth.status;
    if(authStatus === UserState.LOGGED_OUT || authStatus === UserState.UNSET) {
      return {loggedOut: true, authorized: null};
    } else {
      if (permissions === undefined) {
        return {loggedOut: false, authorized: null};
      } else {
        return {loggedOut: false, authorized: selectUserPermission(state, permissions)};
      }
    }
  }
);


const selectUserPermission = (state, requiredPermission) => {
  const activeWarehouse = state.warehouse.activeId;
  const permissions = state.auth.permissions;

  const type = state.settings.environment.selectedEnvironmentType;
  const id = state.settings.environment.selectedId;

  let relation_id = 0;
  let customer_id = 0;
  
  if(type === ENV_TYPES[100]){
    customer_id = id;
  } else if(type === ENV_TYPES[200]) {
    relation_id = id;
  }

  if(!permissions) return true;

  let permissionObject = permissions.find(permission => ((customer_id > 0 && 
    permission.customerId === parseInt(customer_id) && permission.relationId === null &&
      permission.warehouseId === parseInt(activeWarehouse)) ||
      (relation_id > 0 && permission.relationId === parseInt(relation_id)))
  );


  if (permissionObject) {
      if (permissionObject.admin || requiredPermission.some(p => permissionObject.permissions.includes(p))) {
          return true;
      }
  } else {
      // Check general warehouse permission
      let permissionObject = permissions.find(permission => (customer_id > 0 && 
          permission.customerId === parseInt(customer_id) &&
          permission.warehouseId === -1)
      );

      if (permissionObject?.admin || requiredPermission.some(p => permissionObject?.permissions.includes(p))) {
          return true;
      }
  }

  return false;
};

const isAdminUser = (state) => {
  const activeWarehouse = state.warehouse.activeId;
  const permissions = state.auth.permissions;

  const type = state.settings.environment.selectedEnvironmentType;
  const id = state.settings.environment.selectedId;

  let relation_id = 0;
  let customer_id = 0;
  
  if(type === ENV_TYPES[100]){
    customer_id = id;
  } else if(type === ENV_TYPES[200]) {
    relation_id = id;
  }

  if(!permissions) return true;

  let permissionObject = permissions.find(permission => ((customer_id > 0 && 
    permission.customerId === parseInt(customer_id) && permission.relationId === null &&
      permission.warehouseId === parseInt(activeWarehouse)) ||
      (relation_id > 0 && permission.relationId === parseInt(relation_id)))
  );


  if (permissionObject) {
      if (permissionObject.admin) {
          return true;
      }
  } else {
      // Check general warehouse permission
      let permissionObject = permissions.find(permission => (customer_id > 0 && 
          permission.customerId === parseInt(customer_id) &&
          permission.warehouseId === -1)
      );

      if (permissionObject?.admin) {
          return true;
      }
  }

  return false;
};


export const hasPermissionForCustomer = ({
  permissions = [],
  customerId,
  warehouseId,
  requiredPermissions = []
}) => {
  if (!permissions || !customerId || !warehouseId) return false;

  let permission = permissions.find(p =>
    p.customerId === parseInt(customerId) &&
    p.relationId === null &&
    p.warehouseId === parseInt(warehouseId)
  );

  if (!permission) {
    permission = permissions.find(p =>
      p.customerId === parseInt(customerId) &&
      p.relationId === null &&
      p.warehouseId === -1
    );
  }

  return permission?.admin || requiredPermissions.some(p => permission?.permissions.includes(p));
};

export const selectAuth = state => state.auth;
export const selectAuthStatus = state => state.auth.status;
export const selectCustomers = (state) => state.auth.customers;
export const selectRelations = (state) => state.auth.relations;
export const selectUser = (state) => state.auth.user;
export const selectDefaultEnvironment = (state) => state.auth.user?.defaultEnvironment ?? null;
export const relationBlocked = state => (state.auth.customers?.length === 0 &&  state.auth.relations?.length === 1 && state.auth.relations?.[0]?.isBlocked) || false;

export const { updateToken, logout,updateAuthData,removeRelationById,removeCustomerById,setDefaultEnvironment } = authSlice.actions;
export { selectUserPermission,isAdminUser };
export default authSlice.reducer;
